import React, { createContext, useContext, useState, useEffect } from 'react';

const B2BOrderContext = createContext();

export const B2BOrderProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);

  useEffect(() => {
    // Load orders from localStorage
    const savedOrders = localStorage.getItem('b2b_orders');
    if (savedOrders) {
      setOrders(JSON.parse(savedOrders));
    }
  }, []);

  // Generate order number
  const generateOrderNumber = () => {
    const year = new Date().getFullYear();
    const count = orders.length + 1;
    return `DS-${year}-${String(count).padStart(4, '0')}`;
  };

  // Create new order
  const createOrder = (orderData) => {
    const newOrder = {
      id: Date.now().toString(),
      orderNumber: generateOrderNumber(),
      ...orderData,
      status: 'pending',
      createdAt: new Date().toISOString(),
      history: [
        {
          action: 'created',
          timestamp: new Date().toISOString(),
          details: 'تم إنشاء الطلب'
        }
      ]
    };

    const updatedOrders = [...orders, newOrder];
    setOrders(updatedOrders);
    localStorage.setItem('b2b_orders', JSON.stringify(updatedOrders));
    
    return newOrder;
  };

  // Update order
  const updateOrder = (orderId, updates, changeDescription) => {
    const updatedOrders = orders.map(order => {
      if (order.id === orderId) {
        const historyEntry = {
          action: 'updated',
          timestamp: new Date().toISOString(),
          details: changeDescription,
          previousItems: order.items,
          newItems: updates.items || order.items
        };

        return {
          ...order,
          ...updates,
          history: [...order.history, historyEntry]
        };
      }
      return order;
    });

    setOrders(updatedOrders);
    localStorage.setItem('b2b_orders', JSON.stringify(updatedOrders));
  };

  // Confirm order
  const confirmOrder = (orderId) => {
    const updatedOrders = orders.map(order => {
      if (order.id === orderId) {
        return {
          ...order,
          status: 'confirmed',
          confirmedAt: new Date().toISOString(),
          history: [
            ...order.history,
            {
              action: 'confirmed',
              timestamp: new Date().toISOString(),
              details: 'تم تأكيد الطلب نهائياً'
            }
          ]
        };
      }
      return order;
    });

    setOrders(updatedOrders);
    localStorage.setItem('b2b_orders', JSON.stringify(updatedOrders));
  };

  // Update order status (for admin)
  const updateOrderStatus = (orderId, status) => {
    const statusNames = {
      pending: 'جديد',
      confirmed: 'مؤكد',
      preparing: 'تحت التجهيز',
      completed: 'مكتمل',
      cancelled: 'ملغي'
    };

    const updatedOrders = orders.map(order => {
      if (order.id === orderId) {
        return {
          ...order,
          status,
          history: [
            ...order.history,
            {
              action: 'status_change',
              timestamp: new Date().toISOString(),
              details: `تم تغيير الحالة إلى: ${statusNames[status]}`
            }
          ]
        };
      }
      return order;
    });

    setOrders(updatedOrders);
    localStorage.setItem('b2b_orders', JSON.stringify(updatedOrders));
  };

  // Get orders by user
  const getOrdersByUser = (userId) => {
    return orders.filter(order => order.userId === userId);
  };

  // Get orders by date range
  const getOrdersByDateRange = (startDate, endDate) => {
    return orders.filter(order => {
      const orderDate = new Date(order.createdAt);
      return orderDate >= startDate && orderDate <= endDate;
    });
  };

  // Get weekly report
  const getWeeklyReport = () => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - 7);

    const weeklyOrders = orders.filter(order => {
      const orderDate = new Date(order.createdAt);
      return orderDate >= startOfWeek && order.status !== 'cancelled';
    });

    // Group by customer
    const customerReports = {};
    weeklyOrders.forEach(order => {
      if (!customerReports[order.userId]) {
        customerReports[order.userId] = {
          customerName: order.customerName,
          businessName: order.businessName,
          businessType: order.businessType,
          totalAmount: 0,
          items: {}
        };
      }

      customerReports[order.userId].totalAmount += order.totalAmount;
      
      order.items.forEach(item => {
        if (!customerReports[order.userId].items[item.productId]) {
          customerReports[order.userId].items[item.productId] = {
            name: item.productName,
            quantity: 0,
            unit: item.unit
          };
        }
        customerReports[order.userId].items[item.productId].quantity += item.quantity;
      });
    });

    return customerReports;
  };

  return (
    <B2BOrderContext.Provider value={{
      orders,
      currentOrder,
      setCurrentOrder,
      createOrder,
      updateOrder,
      confirmOrder,
      updateOrderStatus,
      getOrdersByUser,
      getOrdersByDateRange,
      getWeeklyReport
    }}>
      {children}
    </B2BOrderContext.Provider>
  );
};

export const useB2BOrders = () => {
  const context = useContext(B2BOrderContext);
  if (!context) {
    throw new Error('useB2BOrders must be used within a B2BOrderProvider');
  }
  return context;
};
